import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { fadeInUp, prefersReducedMotion } from '../lib/visualEffects';

// Minimal notification system inspired by admin-socap, without external state libs
const NotificationsContext = React.createContext(null);

const DEFAULT_SETTINGS = {
  enabled: true,
  durationMs: 4000,
  position: 'top-right', // 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  maxItems: 4,
};

function typeStyles(type) {
  if (type === 'success') return { bg: 'bg-emerald-50', text: 'text-emerald-900', ring: 'ring-emerald-200' };
  if (type === 'warning') return { bg: 'bg-amber-50', text: 'text-amber-900', ring: 'ring-amber-200' };
  if (type === 'error') return { bg: 'bg-rose-50', text: 'text-rose-900', ring: 'ring-rose-200' };
  return { bg: 'bg-blue-50', text: 'text-blue-900', ring: 'ring-blue-200' }; // info
}

export function useNotifications() {
  const ctx = React.useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}

export function NotificationsProvider({ children, settings: settingsProp }) {
  const settings = React.useMemo(() => ({ ...DEFAULT_SETTINGS, ...(settingsProp || {}) }), [settingsProp]);
  const [items, setItems] = React.useState([]);
  const timeoutsRef = React.useRef(new Map());

  const dismiss = React.useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    const t = timeoutsRef.current.get(id);
    if (t) { clearTimeout(t); timeoutsRef.current.delete(id); }
  }, []);

  const clear = React.useCallback(() => {
    setItems([]);
    for (const t of timeoutsRef.current.values()) clearTimeout(t);
    timeoutsRef.current.clear();
  }, []);

  const push = React.useCallback((msg, opts = {}) => {
    if (!settings.enabled) return null;
    const id = Date.now() + Math.random();
    const entry = { id, msg: String(msg), type: opts.type || 'info' };
    setItems((prev) => {
      const next = [entry, ...prev];
      // Cap list length
      if (next.length > settings.maxItems) next.length = settings.maxItems;
      return next;
    });
    const dur = Number(opts.durationMs ?? settings.durationMs);
    if (Number.isFinite(dur) && dur > 0) {
      const t = setTimeout(() => dismiss(id), dur);
      timeoutsRef.current.set(id, t);
    }
    return id;
  }, [settings.enabled, settings.maxItems, settings.durationMs, dismiss]);

  const value = React.useMemo(() => ({ items, push, dismiss, clear, settings }), [items, push, dismiss, clear, settings]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <Toasts items={items} onDismiss={dismiss} position={settings.position} />
    </NotificationsContext.Provider>
  );
}

function IconFor({ type }) {
  if (type === 'success') return <CheckCircle2 className="w-4 h-4" />;
  if (type === 'warning') return <AlertCircle className="w-4 h-4" />;
  if (type === 'error') return <AlertCircle className="w-4 h-4" />;
  return <Info className="w-4 h-4" />;
}

function Toasts({ items, onDismiss, position }) {
  const base = 'fixed z-[60] p-4 space-y-2';
  const positionClass = position === 'top-right' ? 'top-2 right-2'
    : position === 'top-left' ? 'top-2 left-2'
    : position === 'bottom-right' ? 'bottom-2 right-2' : 'bottom-2 left-2';
  const reduceMotion = React.useMemo(() => prefersReducedMotion(), []);

  return (
    <div className={`${base} ${positionClass}`} aria-live="polite" role="status">
      <AnimatePresence initial={false}>
        {items.map((it) => {
          const s = typeStyles(it.type);
          return (
            <motion.div
              key={it.id}
              layout
              className={`flex items-start gap-2 rounded-md px-3 py-2 ring-1 ${s.bg} ${s.text} ${s.ring}`}
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: reduceMotion ? 0 : 0.25 }}
              whileHover={reduceMotion ? undefined : { scale: 1.02 }}
            >
              <div className="mt-0.5"><IconFor type={it.type} /></div>
              <div className="text-sm leading-snug pr-6">{it.msg}</div>
              <button onClick={() => onDismiss(it.id)} className="ml-auto text-current/70 hover:text-current" aria-label="Dismiss notification">
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

export default NotificationsProvider;
